import {useCallback, ReactElement} from 'react';
import {Button} from '@shopify/polaris';
import {useToggle} from '@shopify/react-hooks';
import {useI18n} from '@shopify/react-i18n';

import {useListFormatter, JoinFormat} from './use-list-formatter';

interface RenderToggleParams {
  truncated: boolean;
  onClick: () => void;
  ariaProps: {
    ariaExpanded: boolean;
    ariaControls: string;
  };
}

export interface TruncatedListOptions<T> {
  maxLength?: number;
  showTruncationToggle?: boolean;
  ariaExpanderId?: string;
  renderToggleButton?: (params: RenderToggleParams) => ReactElement<T>;
}

const scope = ['TruncatedList'];

export function useTruncatedList<T extends {onClick: () => void}>({
  showTruncationToggle = true,
  maxLength = 2,
  ariaExpanderId = 'truncated-list',
  renderToggleButton,
}: TruncatedListOptions<T> = {}) {
  const [i18n] = useI18n();
  const {formatList} = useListFormatter();
  const {value: showingAll, toggle} = useToggle(false);

  return useCallback(
    (items: string[]) => {
      if (items.length <= maxLength) {
        return formatList(items, JoinFormat.And);
      }

      const truncatedList = items.slice(0, maxLength);
      const truncatedListEnding = i18n.translate(
        'more',
        {scope},
        {count: items.length - truncatedList.length},
      );
      const truncatedListWithEnding = [...truncatedList, truncatedListEnding];

      if (!showTruncationToggle) {
        return formatList(truncatedListWithEnding, JoinFormat.And);
      }

      const toggledList = showingAll ? items : truncatedListWithEnding;
      const ariaProps = {
        ariaExpanded: showingAll,
        ariaControls: ariaExpanderId,
      };

      const seeAction = renderToggleButton ? (
        renderToggleButton({truncated: !showingAll, onClick: toggle, ariaProps})
      ) : (
        <Button variant="plain" onClick={toggle} {...ariaProps}>
          {i18n.translate(showingAll ? 'seeLess' : 'seeAll', {scope})}
        </Button>
      );

      return i18n.translate(
        'list',
        {scope},
        {
          seeAction,
          formattedList: (
            <span id={ariaExpanderId}>
              {formatList(toggledList, JoinFormat.And)}
            </span>
          ),
        },
      );
    },
    [
      showingAll,
      toggle,
      i18n,
      showTruncationToggle,
      ariaExpanderId,
      formatList,
      maxLength,
      renderToggleButton,
    ],
  );
}
